'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { preload } from 'react-dom';

import { cn } from '@/lib/cn';

import {
  DOTS_PER_FRAME,
  FRAME_COUNT,
  GRID_H,
  GRID_W,
} from './dot-matrix-decode';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

let reducedMotionMql: MediaQueryList | undefined;
// one shared MediaQueryList instead of a fresh parse on every snapshot read
const getReducedMotionMql = () =>
  (reducedMotionMql ??= window.matchMedia(REDUCED_MOTION_QUERY));

function subscribeReducedMotion(onChange: () => void) {
  const mq = getReducedMotionMql();
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

// Server snapshot false so the canvas is in the SSR HTML; reduced-motion users
// unmount it after hydration.
function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => getReducedMotionMql().matches,
    () => false
  );
}

// Frame payload and encoding: see dot-matrix-decode.ts. This module owns the
// fetch, the worker round-trip, and the canvas replay.
const NATIVE_W = 1920;
const NATIVE_H = 1080;
const DOT_SPACING = 12;
const DOT_RADIUS = 1;
const LOOP_SECONDS = 12;
// gzip stream despite the .bin name; fetched once and decoded module-wide
const FRAMES_URL = '/video/dot-matrix-frames.bin';
// after a fetch/decode failure, don't re-download on every remount
const RETRY_COOLDOWN_MS = 60_000;

// gunzipped payload goes in transferred, decoded frames come back transferred;
// the decode loop itself never runs on the main thread
function decodeInWorker(buffer: ArrayBuffer): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL('./dot-matrix-decode.worker.ts', import.meta.url)
    );
    const fail = (message: string) => {
      worker.terminate();
      reject(new Error(message));
    };
    worker.onmessage = (
      event: MessageEvent<Uint8Array | { error: string }>
    ) => {
      if (event.data instanceof Uint8Array) {
        worker.terminate();
        resolve(event.data);
      } else {
        fail(event.data.error);
      }
    };
    worker.onerror = (event) => fail(event.message || 'decode worker failed');
    worker.postMessage(buffer, [buffer]);
  });
}

let framesPromise: Promise<Uint8Array> | null = null;
let lastFailureAt = 0;

// null = not available right now (unsupported browser, or cooling down after a
// failure); the canvas then simply never becomes visible and the scrim covers.
function loadFrames(): Promise<Uint8Array> | null {
  if (typeof DecompressionStream === 'undefined') return null;
  if (!framesPromise && Date.now() - lastFailureAt < RETRY_COOLDOWN_MS)
    return null;
  framesPromise ??= fetch(FRAMES_URL)
    .then((res) => {
      if (!res.ok || !res.body)
        throw new Error(`frames fetch failed: ${res.status}`);
      return new Response(
        res.body.pipeThrough(new DecompressionStream('gzip'))
      ).arrayBuffer();
    })
    .then(decodeInWorker);
  // allow a later retry instead of caching the failure forever
  framesPromise.catch(() => {
    lastFailureAt = Date.now();
    framesPromise = null;
  });
  return framesPromise;
}

// kick the fetch off at module evaluation so it races hydration instead of
// waiting for the first mount's effect; reduced-motion users never draw it,
// so don't download 1.9 MB on their behalf
if (typeof window !== 'undefined' && !getReducedMotionMql().matches) {
  loadFrames();
}

// brightness quantized to 16 levels: indistinguishable under the scrim, and
// caps fill batches per frame at 16 instead of a worst case of 253
const QUANT_LEVELS = 16;
const COLOR_LUT = Array.from({ length: QUANT_LEVELS }, (_, level) => {
  const v = level * 16 + 8; // bucket midpoint
  return `rgb(${v},${v},${v})`;
});

// Looping dot-matrix backdrop behind nextgen surfaces; skipped for reduced-motion.
// `className` scopes it (shell's full-screen -z-20 vs a card-local backdrop).
// Invisible (opacity 0) until the first frame is drawn, so unsupported browsers
// and failed loads degrade to the scrim-covered layer like the old <video> did.
export function DotMatrixBackground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  if (!reducedMotion) {
    // hoisted into <head> during SSR so the download starts at HTML parse,
    // before any JS; anonymous matches fetch()'s default same-origin
    // credentials. The server snapshot is always false, so first-load SSR
    // still emits the hint for reduced-motion users; accepted gap, closing it
    // would need a persisted client signal.
    preload(FRAMES_URL, { as: 'fetch', crossOrigin: 'anonymous' });
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (reducedMotion || !canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    // narrowed binding: TS drops the null-check narrowing inside closures
    const surface = canvas;

    let frames: Uint8Array | null = null;
    let disposed = false;
    let raf = 0;
    let running = false;
    let hidden = document.hidden;
    let intersecting = false;
    let lastFrame = -1;
    let width = 0;
    let height = 0;
    let scale = 1;
    let offsetX = 0;
    let offsetY = 0;
    // cull rows/cols that the cover-crop pushes outside the canvas
    let rowMin = 0;
    let rowMax = GRID_H - 1;
    let colMin = 0;
    let colMax = GRID_W - 1;
    // counting sort scratch: dots grouped by quantized brightness so each
    // frame issues at most QUANT_LEVELS fills instead of one per dot
    const bucketCounts = new Uint16Array(QUANT_LEVELS);
    const bucketOffsets = new Uint32Array(QUANT_LEVELS);
    const sortedDots = new Uint32Array(DOTS_PER_FRAME);

    function resize() {
      const rect = surface.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1 || !ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      surface.width = Math.floor(width * dpr);
      surface.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scale = Math.max(width / NATIVE_W, height / NATIVE_H); // cover, centered
      offsetX = (width - NATIVE_W * scale) / 2;
      offsetY = (height - NATIVE_H * scale) / 2;
      const step = DOT_SPACING * scale;
      const r = DOT_RADIUS * scale;
      rowMin = Math.max(0, Math.floor((-offsetY - r) / step));
      rowMax = Math.min(GRID_H - 1, Math.ceil((height - offsetY + r) / step));
      colMin = Math.max(0, Math.floor((-offsetX - r) / step));
      colMax = Math.min(GRID_W - 1, Math.ceil((width - offsetX + r) / step));
      if (frames && lastFrame >= 0) draw(lastFrame); // repaint at the new size
    }

    function draw(frame: number) {
      if (!frames || !width || !height || !ctx) return;
      const base = frame * DOTS_PER_FRAME;
      bucketCounts.fill(0);
      for (let row = rowMin; row <= rowMax; row++) {
        for (let col = colMin; col <= colMax; col++) {
          const value = frames[base + row * GRID_W + col];
          if (value > 2) bucketCounts[value >> 4]++;
        }
      }
      let offset = 0;
      for (let level = 0; level < QUANT_LEVELS; level++) {
        bucketOffsets[level] = offset;
        offset += bucketCounts[level];
      }
      for (let row = rowMin; row <= rowMax; row++) {
        for (let col = colMin; col <= colMax; col++) {
          const value = frames[base + row * GRID_W + col];
          if (value > 2)
            sortedDots[bucketOffsets[value >> 4]++] = row * GRID_W + col;
        }
      }
      ctx.fillStyle = '#020202';
      ctx.fillRect(0, 0, width, height);
      const radius = DOT_RADIUS * scale;
      const step = DOT_SPACING * scale;
      const TAU = Math.PI * 2;
      let cursor = 0;
      for (let level = 0; level < QUANT_LEVELS; level++) {
        const count = bucketCounts[level];
        if (!count) continue;
        ctx.fillStyle = COLOR_LUT[level];
        ctx.beginPath();
        for (const end = cursor + count; cursor < end; cursor++) {
          const dot = sortedDots[cursor];
          const x = offsetX + (dot % GRID_W) * step;
          const y = offsetY + Math.floor(dot / GRID_W) * step;
          ctx.moveTo(x + radius, y);
          ctx.arc(x, y, radius, 0, TAU);
        }
        ctx.fill();
      }
      if (surface.style.opacity !== '1') surface.style.opacity = '1';
    }

    function tick() {
      // loop phase off the wall clock: resumes mid-loop after a pause and
      // keeps simultaneous mounts (shell + card-local) in sync
      const elapsed = (performance.now() / 1000) % LOOP_SECONDS;
      const frame =
        Math.floor((elapsed / LOOP_SECONDS) * FRAME_COUNT) % FRAME_COUNT;
      // content is 30fps; skip redundant redraws on 60Hz+ displays
      if (frame !== lastFrame) {
        draw(frame);
        lastFrame = frame;
      }
      raf = requestAnimationFrame(tick);
    }

    // single gate for the three playback signals (tab visibility,
    // viewport intersection, frames loaded); any one alone must not play
    function syncPlayback() {
      const shouldPlay = !hidden && intersecting && frames !== null;
      if (shouldPlay && !running) {
        running = true;
        raf = requestAnimationFrame(tick);
      } else if (!shouldPlay && running) {
        running = false;
        cancelAnimationFrame(raf);
      }
    }

    function onVisibilityChange() {
      hidden = document.hidden;
      syncPlayback();
    }

    // the browser wipes all context state on a restored 2d context; resize()
    // re-applies the transform and repaints the last frame
    function onContextRestored() {
      resize();
    }

    // ResizeObserver misses a window moving to a monitor with a different DPR
    // but the same CSS size; re-arm a one-shot resolution query per DPR value
    let unwatchDpr: (() => void) | undefined;
    function watchDpr() {
      const mq = window.matchMedia(
        `(resolution: ${window.devicePixelRatio}dppx)`
      );
      const onChange = () => {
        resize();
        watchDpr();
      };
      mq.addEventListener('change', onChange, { once: true });
      unwatchDpr = () => mq.removeEventListener('change', onChange);
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    // pause while off-screen (card-local mounts) or in a hidden tab
    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        intersecting = entry.isIntersecting;
      }
      syncPlayback();
    });
    intersectionObserver.observe(canvas);
    document.addEventListener('visibilitychange', onVisibilityChange);
    canvas.addEventListener('contextrestored', onContextRestored);
    watchDpr();

    resize();
    loadFrames()
      ?.then((decoded) => {
        if (disposed) return;
        frames = decoded;
        syncPlayback();
      })
      // the canvas never turned visible, so the scrim-covered layer shows,
      // same as browsers without AV1 did with the old video
      .catch((error) => console.error('dot-matrix background:', error));

    return () => {
      disposed = true;
      running = false;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      canvas.removeEventListener('contextrestored', onContextRestored);
      unwatchDpr?.();
    };
  }, [reducedMotion]);

  if (reducedMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      // contain: nothing outside the canvas depends on its layout or paint
      style={{ opacity: 0, contain: 'strict' }}
      className={cn(
        'pointer-events-none absolute inset-0 size-full invert dark:invert-0',
        className
      )}
    />
  );
}
